import { Box, Chip, Typography, alpha } from '@mui/material';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BoltIcon from '@mui/icons-material/Bolt';
import { useSyncExternalStore } from 'react';
import { AppShell, type NavItem } from './AppShell';
import { useAuth } from '../hooks/useAuth';
import { useNotificationsPoller } from '../hooks/useNotificationsPoller';

const NAV: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: <SpaceDashboardIcon /> },
  { label: 'Trade', to: '/trade', icon: <CandlestickChartIcon /> },
  { label: 'Deribit', to: '/trade/deribit', icon: <BoltIcon /> },
  { label: 'Orders', to: '/orders', icon: <ReceiptLongIcon /> },
  { label: 'P&L', to: '/pnl', icon: <TrendingUpIcon /> },
];

/** Browser network status — data stays fresh via polling, so this reflects connectivity. */
function useOnline(): boolean {
  return useSyncExternalStore(
    (cb) => {
      window.addEventListener('online', cb);
      window.addEventListener('offline', cb);
      return () => {
        window.removeEventListener('online', cb);
        window.removeEventListener('offline', cb);
      };
    },
    () => navigator.onLine,
    () => true,
  );
}

export function ClientLayout() {
  const connected = useOnline();
  const { isViewOnly } = useAuth();
  // Surface approval/reject/fill events as toasts the moment they happen.
  useNotificationsPoller();
  // View-only clients can't place orders, so hide the trade tickets from the nav.
  const nav = isViewOnly ? NAV.filter((n) => !n.to.startsWith('/trade')) : NAV;
  const status = (
    <Chip
      size="small"
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: connected ? 'success.main' : 'error.main',
              boxShadow: (t) => `0 0 8px ${connected ? t.palette.success.main : t.palette.error.main}`,
            }}
          />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {connected ? 'Live' : 'Offline'}
          </Typography>
        </Box>
      }
      sx={{
        backgroundColor: (t) =>
          alpha(connected ? t.palette.success.main : t.palette.error.main, 0.12),
        border: (t) => `1px solid ${alpha(connected ? t.palette.success.main : t.palette.error.main, 0.3)}`,
      }}
    />
  );
  return <AppShell nav={nav} status={status} />;
}
